import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MDBBadge, MDBBtn, MDBTypography } from "mdbreact";
import { Categories, HMO, Privileges } from "../../../../../../../../services/fakeDb";
import {
  SETCATEGORY,
  SETPRIVILEGE,
  SETPHYSICIAN,
  SETSOURCE,
  SETSSX,
  RESET_INSOURCE,
  SetCH,
} from "../../../../../../../../services/redux/slices/commerce/pos/services/pos";
import {
  INSOURCE,
  SETSOURCES,
  ADD_AFFILIATED,
  RESET as SOURCERESET,
} from "../../../../../../../../services/redux/slices/assets/providers";
import { BROWSE as BROWSE_BRANCHES } from "../../../../../../../../services/redux/slices/assets/branches";
import { capitalize } from "lodash";
import { fullName } from "../../../../../../../../services/utilities";
import CardCompany from "./cardCompany";
import PickSource from "./source";
import PickSourcePhysician from "./physician";

const contracts = {
  sbc: "Subcontract",
  ssc: "Special Subcontract",
};
const buildHealthCardOptionValue = (card = {}) =>
  `card:${card.provider || ""}:${String(card._id || card.number || "")}`;
const getHealthCardOptionLabel = (card = {}) => {
  const { provider = "", employer = "" } = card;
  if (provider === "phi") {
    return employer ? `PhilHealth - ${employer}` : "PhilHealth";
  }

  const normalized = String(provider || "").toLowerCase();
  const matched = HMO.collections.find(
    ({ code = "", abbr = "", name = "" }) =>
      [code, abbr, name].some(
        (value) => String(value || "").toLowerCase() === normalized,
      ),
  );

  const baseLabel = (
    matched?.abbr ||
    matched?.name ||
    String(provider || "").replace(/\b\w/g, (char) => char.toUpperCase()) ||
    "HMO"
  );

  return employer ? `${baseLabel} - ${employer}` : baseLabel;
};

export default function PosCard() {
  const {
      category,
      privilege,
      customer,
      ssx,
      sourceId,
      formSubmitted,
      isSuccess,
      cardHolder,
    } = useSelector(({ pos }) => pos),
    { collections } = useSelector(({ providers }) => providers),
    { collections: inhouse } = useSelector(({ branches }) => branches),
    { token, activePlatform } = useSelector(({ auth }) => auth),
    { collections: allPhysicians = [] } = useSelector(
      ({ physicians }) => physicians,
    ),
    [physicians, setPhysicians] = useState([]),
    [categories, setCategories] = useState([]),
    [source, setSource] = useState({}),
    [scType, setScType] = useState("rfr"), //Source Type
    dispatch = useDispatch();

  const { branch = {} } = activePlatform;
  const { companyId: company = {} } = branch || {};
  useEffect(() => {
    const fakeDB = localStorage.getItem("activePlatform");
    if (fakeDB) {
      setCategories(JSON.parse(fakeDB)?.branch?.companyId?.pc?.filter(Boolean));
    }
  }, []);

  useEffect(() => {
    dispatch(BROWSE_BRANCHES({ token, key: { companyId: company?._id } }));
  }, [company, dispatch, token]);

  useEffect(() => {
    if (token && activePlatform.branchId) {
      const branchId = activePlatform.branchId;

      // Check if the source data for the specific branchId is already in localStorage
      const storedSource = localStorage.getItem(`source_${branchId}`);

      if (storedSource) {
        // If source data is found in localStorage, use it (parse back to an object)
        const sourceData = JSON.parse(storedSource);

        // Optionally dispatch the source data to update the store
        dispatch(SETSOURCES(sourceData));
      } else {
        // If no data in localStorage, make the server request
        dispatch(
          INSOURCE({
            token,
            key: {
              vendors: activePlatform.branchId,
              status: "approved",
            },
          }),
        )
          .then(({ payload }) => {
            // Assuming the response contains the source data in 'payload'
            const sourceData = payload.payload;
            // console.log("Fetching source data:", sourceData);

            // Store the fetched data in localStorage for future use
            localStorage.setItem(
              `source_${branchId}`,
              JSON.stringify(sourceData),
            );
          })
          .catch((error) => {
            console.error("Error fetching source data:", error);
          });
      }

      // Cleanup function
      return () => {
        dispatch(SOURCERESET());
      };
    }
  }, [token, dispatch, activePlatform]);

  const { _id, privilege: userPrivilege = 0 } = customer,
    didSelect = Boolean(_id);
  const savedHealthCards = Array.isArray(customer?.healthCard)
    ? customer.healthCard.filter(
        ({ provider = "", number = "" }) => provider || number,
      )
    : [];
  const hasMembershipSources = collections.some(({ category = "" }) => category === "mbs");
  const hasContractSources = collections.some(({ category = "" }) => category === "ctr");
  const cardHolderOptions = [
    { value: "", label: "None" },
    ...savedHealthCards.map((card) => {
      return {
        value: buildHealthCardOptionValue(card),
        label: getHealthCardOptionLabel(card),
      };
    }),
    ...(hasMembershipSources ? [{ value: "mbs", label: "Membership" }] : []),
    ...(hasContractSources ? [{ value: "ctr", label: "Contract" }] : []),
  ];
  const currentCardHolderProvider =
    cardHolder?.provider ||
    (cardHolder?.type === "wls"
      ? cardHolder?.company?.name || ""
      : cardHolder?.type || "");
  const selectedCardHolderValue = (() => {
    if (!currentCardHolderProvider) return "";

    if (cardHolder?.type === "phi" || cardHolder?.type === "wls") {
      const matchedCard = savedHealthCards.find(
        (card) =>
          card.provider === currentCardHolderProvider &&
          String(card._id || card.number || "") ===
            String(cardHolder?.company?.ref || ""),
      );

      return matchedCard ? buildHealthCardOptionValue(matchedCard) : "";
    }

    return currentCardHolderProvider;
  })();

  const handleCategory = (category) => {
    const { abbr } = Categories[category];
    const _abbr = [
      "wi",
      "opd",
      "er",
      "cw",
      "pw",
      "bp",
      "mc",
      "prm",
      "sc",
      "sr",
    ].includes(abbr)
      ? "rfr"
      : abbr;
    setScType(_abbr);
    setSource({});
    dispatch(RESET_INSOURCE());
    dispatch(SETCATEGORY(category));
  };
  const handlePrivilege = (privilege) => dispatch(SETPRIVILEGE(privilege));

  const isInhouse = scType === "inhouse";
  //filter the sources by source type
  const _sources = [...collections]?.filter(
    ({ category }) => category === scType,
  );
  //if inhouse set the branches to sources if not get the filtered sources
  const sources = isInhouse
    ? inhouse.filter(({ _id }) => _id !== branch?._id)
    : _sources;

  const getSourceLabel = ({ clients, name = "", displayname = "" } = {}) =>
    isInhouse
      ? displayname || name
      : clients?.displayname || clients?.name || "";
  // takes the source itself, a newly tagged source is not yet in `sources`
  const handleSource = (_source = {}) => {
    // Update the physicians list based on the selected source
    setPhysicians(
      (isInhouse ? _source?.affiliated : _source?.clients?.affiliated) || [],
    );

    handlePhysician(""); // reset the selected pyhisican if change the source

    const { clients, _id: scID = "" } = _source || {};
    setSource(_source || {});
    dispatch(RESET_INSOURCE());
    dispatch(SETSOURCE(isInhouse ? scID : clients?._id));
  };
  const handlePhysician = (physician) => dispatch(SETPHYSICIAN({ physician }));
  const getCIndex = (abbr) =>
    Categories.findIndex(({ abbr: name }) => name === abbr);

  const srcEndPoint =
    Categories[getCIndex(scType)]?.name ||
    (scType === "mbs" ? "Membership" : scType === "ctr" ? "Contract" : "");

  // physicians of the selected source (affiliated or tagged to its branch),
  // or of your own branch when there is no source
  const sourceBranchId = String(
    (isInhouse ? source?._id : source?.clients?._id) ||
      activePlatform.branchId ||
      "",
  );
  const byName = (a, b) =>
    fullName(a.user.fullName).localeCompare(fullName(b.user.fullName));
  // one entry per physician (a user can have a record per branch)
  const withUser = (list = []) => {
    const seen = new Set();
    return list.filter(({ user }) => {
      if (!user?._id || seen.has(user._id)) return false;
      seen.add(user._id);
      return true;
    });
  };
  // filter by branch before de-duplicating, so no branch record is skipped
  const sourcePhysicians = sourceBranchId
    ? withUser([
        ...physicians,
        ...allPhysicians.filter(
          ({ branch }) => String(branch?._id || branch || "") === sourceBranchId,
        ),
      ]).sort(byName)
    : [];
  // keep a newly tagged physician on the source and its POS cache
  const handleTagged = (physician) => {
    setPhysicians((prev) => [physician, ...(prev || [])]);
    if (!isInhouse && source?._id)
      dispatch(
        ADD_AFFILIATED({ isRegister: true, source: { _id: source._id }, physician }),
      );
  };
  return (
    <>
      <div>
        <div className="patient-form">
          <span title="Signs and Symptoms">Signs and Symptoms</span>
          <input
            placeholder="3 days fever, headache (etc)..."
            type="text"
            value={ssx}
            onChange={({ target }) => dispatch(SETSSX(target.value))}
          />
        </div>
        <div className="patient-form mt-2">
          <span>Privilege</span>
          <select
            disabled={!didSelect}
            value={privilege}
            onChange={({ target }) => handlePrivilege(Number(target.value))}
          >
            {Privileges.map((privilege, index) => {
              let disabled = false;
              // Ensure only valid seniors can select "Senior Citizen" (index 2)
              if (index === 2 && userPrivilege !== 2) {
                disabled = true;
              }

              return (
                <option
                  disabled={disabled}
                  key={`privilege-${index}`}
                  value={index}
                >
                  {privilege}
                </option>
              );
            })}
          </select>
        </div>
        <div className="patient-form mt-2">
          .<span>Category</span>
          <select
            disabled={!didSelect}
            value={category}
            onChange={({ target }) => handleCategory(Number(target.value))}
          >
            {[0, ...(categories || [])]?.map((c, index) => {
              const { name = "", color = "" } = Categories[c] || {};
              return (
                <option value={c} key={`category-${index}`} style={{ color }}>
                  {name}
                </option>
              );
            })}
          </select>
        </div>
        <div className="patient-form mt-2">
          <span>Card Holder</span>
          <select
            disabled={!didSelect}
            value={selectedCardHolderValue}
            onChange={({ target }) => {
              const value = target.value;

              if (value.startsWith("card:")) {
                const [, provider = "", ref = ""] = value.split(":");
                const selectedHealthCard = savedHealthCards.find(
                  (card) =>
                    card.provider === provider &&
                    String(card._id || card.number || "") === ref,
                );

                setSource({});
                setPhysicians([]);
                dispatch(RESET_INSOURCE());
                dispatch(
                  SetCH({
                    provider,
                    type: provider === "phi" ? "phi" : "wls",
                    company: {
                      name: provider,
                      ref,
                      employer: selectedHealthCard?.employer || "",
                      label: getHealthCardOptionLabel(selectedHealthCard || {}),
                    },
                    tier: "",
                  }),
                );
                return;
              }

              const haveSource = ["mbs", "ctr"].includes(value);
              if (haveSource) {
                setScType(value);
              }
              if (!haveSource && ["mbs", "ctr"].includes(scType)) {
                setScType("rfr");
              }
              setSource({});
              setPhysicians([]);
              dispatch(RESET_INSOURCE());
              dispatch(SetCH({ provider: value }));
            }}
          >
            {cardHolderOptions.map(({ value, label }, index) => {
              return (
                <option
                  value={value}
                  key={`category-${index}`}
                >
                  {label}
                </option>
              );
            })}
          </select>
        </div>
        <CardCompany />
        <div className="mt-2">
          <span style={{ fontSize: "0.9rem", fontWeight: 400 }}>
            Source Type:
          </span>
          <div className="d-flex align-items-center ">
            {[
              { text: "Referral", value: "rfr" },
              { text: "Inhouse", value: "inhouse" },
              { text: "Contract", value: "ctr" },
              { text: "Membership", value: "mbs" },
            ].map(({ text, value }, index) => (
              <MDBBtn
                disabled={!didSelect}
                size="sm"
                className={`px-2 py-${scType === value ? "2" : "1"}`}
                outline={scType !== value}
                color="info"
                key={index}
                onClick={() => {
                  setScType(value);
                  setSource({});
                  setPhysicians([]);
                  dispatch(RESET_INSOURCE());
                }}
              >
                {text}
              </MDBBtn>
            ))}
          </div>
        </div>
        <div className="patient-form mt-2">
          <span className="d-block">Source</span>
          <PickSource
            disabled={!didSelect}
            scType={scType}
            srcName={isInhouse ? "Inhouse" : srcEndPoint}
            sources={sources}
            value={source}
            getLabel={getSourceLabel}
            onSelect={handleSource}
          />
        </div>
        <div className="patient-form mt-2">
          {/* // wls */}

          {category === 7 && sourceId && scType === "mbs" && (
            <span>
              Membership :
              <MDBBadge
                color="warning"
                className="ml-1"
                style={{ fontSize: "0.8rem" }}
              >
                {capitalize(source?.membership)}{" "}
              </MDBBadge>
            </span>
          )}
          {category === 8 && sourceId && (
            <span>
              Contract :
              <MDBBadge
                color="warning"
                className="ml-1"
                style={{ fontSize: "0.8rem" }}
              >
                {capitalize(contracts[source?.contract])}
              </MDBBadge>
            </span>
          )}
        </div>

        {/* {source ? ( */}
        <div className="patient-form mt-2">
          <span className="d-block  ">Physician</span>
          <PickSourcePhysician
            disabled={!didSelect}
            tagged={sourcePhysicians}
            branchId={sourceBranchId}
            sourceName={source?._id ? getSourceLabel(source) : ""}
            onTagged={handleTagged}
            onChange={(value) =>
              handlePhysician({
                ...value,
                source: {
                  _id: source?._id,
                  branch: isInhouse ? source?._id : source?.clients?._id,
                },
              })
            }
            formSubmitted={formSubmitted}
            isSuccess={isSuccess}
          />
        </div>
      </div>
      {!_id && (
        <MDBTypography note noteColor="info" className="mt-3 mb-0">
          Please search a patron first.
        </MDBTypography>
      )}
    </>
  );
}
